"use client";

import { useRouter } from "next/navigation";
import { Button, Text } from "@focal/ui-kit";
import { routes } from "@/constants/routes";
import { useDraftEmails } from "@/hooks/useDraftEmails";

interface OpenInMailActionProps {
  /** Runs the "Open in mail" action — normally a `useOpenInMail` caller. */
  onOpenInMail: () => void;
  /**
   * Disables "Open in mail" for reasons the call site owns (draft POST in
   * flight, recipients still loading). Ignored in the connect state.
   */
  disabled?: boolean;
  "data-cy"?: string;
}

/**
 * The primary action on every email composer footer — "Open in mail", or a
 * "Connect email" CTA when the advisor has no email account to draft into.
 *
 * Drafting requires a connected email provider (Gmail/Outlook), so there is
 * nothing for "Open in mail" to do without one. Rather than let the advisor
 * click through to a failure, the button becomes a route to Email settings —
 * where the account is connected and the signature configured. Mirrors the
 * "Connect CRM" CTA on the Summary and Tasks tabs.
 */
export const OpenInMailAction = ({
  onOpenInMail,
  disabled,
  "data-cy": dataCy,
}: OpenInMailActionProps) => {
  const router = useRouter();
  const { mailProvider, isLoading } = useDraftEmails();

  const hasEmailAccount = !!mailProvider?.account.supportsEmail;

  if (!isLoading && !hasEmailAccount) {
    return (
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="default"
          onClick={() => router.push(routes.settings.email)}
          data-cy="connect-email-button"
        >
          Connect email
        </Button>
        <Text
          variant="p2"
          textColor="secondary"
          className="italic truncate min-w-0"
        >
          Connect your email to create drafts.
        </Text>
      </div>
    );
  }

  return (
    <Button
      variant="default"
      onClick={onOpenInMail}
      // Hold the action until the integration lookup settles, so a
      // not-yet-resolved account can't be read as "none connected" and send
      // the advisor down the legacy compose path.
      disabled={disabled || isLoading}
      data-cy={dataCy}
    >
      Open in mail
    </Button>
  );
};
